import Big from "big.js";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import { $deepPopulateItemMetadata, $isOrderUnpaid } from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyCardSurchargeToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	cardSurcharge,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	cardSurcharge: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	orderItems = orderItems.map((orderItem) => {
		const grossSales = bigMath.sub(
			orderItem.subtotal,
			orderItem.subtotal_addons.offer.amount,
			orderItem.subtotal_addons.discount.amount,
		);
		const addedVatScRate = bigMath.add(
			orderItem.original_price_addons.vat.percentage,
			orderItem.original_price_addons.service_charge.percentage,
		);
		const cardSurchargeAmount = new Big(
			new Big(
				new Big(grossSales).sub(
					new Big(grossSales).div(new Big(1).add(addedVatScRate)),
				),
			).mul(cardSurcharge),
		)
			.div(addedVatScRate || 1)
			.round(2)
			.toNumber();

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			card_surcharge: {
				percentage: cardSurcharge,
				amount: cardSurchargeAmount,
			},
		};

		return { ...orderItem, subtotal_addons };
	});

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const grossSales = bigMath.sub(
				orderItemOption.subtotal,
				orderItemOption.subtotal_addons.offer.amount,
				orderItemOption.subtotal_addons.discount.amount,
			);
			const addedVatScRate = bigMath.add(
				orderItemOption.original_price_addons.vat.percentage,
				orderItemOption.original_price_addons.service_charge.percentage,
			);
			const cardSurchargeAmount = new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(cardSurcharge),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber();

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					card_surcharge: {
						percentage: cardSurcharge,
						amount: cardSurchargeAmount,
					},
				},
			};
		}),
	}));

	const card_surcharge_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.card_surcharge.amount),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal,
		card_surcharge_amount,
	}).$order;
};

export const applyCardSurchargeToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders,
	cardSurcharge,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	cardSurcharge: number;
	skipPaidCheck?: boolean;
}) => {
	return orders
		.filter((o) => (skipPaidCheck ? true : $isOrderUnpaid(o)))
		.map((order) => applyCardSurchargeToOrder({ order, cardSurcharge }));
};
